import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { eur, artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, setCheckoutOpen, removeFromCart } = useStore();
  const { t, lang } = useLang();
  const total = cart.reduce((s, i) => s + i.price, 0);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[70] bg-ink/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[75] flex h-full w-full max-w-md flex-col border-l border-bone/10 bg-ink-2"
          >
            <div className="flex items-center justify-between border-b border-bone/10 px-6 py-5">
              <div>
                <p className="font-serif text-xl text-bone">{t("cart.title")}</p>
                <p className="mt-1 font-mono text-[9px] tracking-[0.3em] text-bone-muted">
                  {t("cart.subtitle")}
                </p>
              </div>
              <button
                data-testid="cart-close-button"
                onClick={() => setCartOpen(false)}
                className="text-bone-dim transition-colors hover:text-brass"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {cart.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <span className="tick" style={{ position: "relative" }} />
                  <p className="font-serif text-2xl italic text-bone-dim">{t("cart.emptyTitle")}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-bone-muted">
                    {t("cart.emptyNote")}
                  </p>
                </div>
              ) : (
                <ul className="space-y-6">
                  {cart.map((item) => (
                    <li
                      key={item.artwork_id}
                      data-testid={`cart-item-${item.artwork_id}`}
                      className="flex gap-4 border border-bone/10 bg-ink p-3"
                    >
                      <div className="h-24 w-20 shrink-0 overflow-hidden border border-bone/10">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bp-grid-fine">
                            <span className="font-mono text-[8px] text-bone-muted">N/A</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col justify-between py-1">
                        <div>
                          <p className="font-serif text-base leading-tight text-bone">{artText(item, "title", lang)}</p>
                          <p className="mt-1 font-mono text-[9px] tracking-[0.2em] text-brass">{item.dwg_no}</p>
                          <p className="mt-1 font-mono text-[9px] tracking-[0.15em] text-bone-muted">
                            {item.dimensions}
                          </p>
                        </div>
                        <p className="font-mono text-sm text-bone">{eur(item.price)}</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.artwork_id}`}
                        onClick={() => removeFromCart(item.artwork_id)}
                        className="self-start p-1 text-bone-muted transition-colors hover:text-brass"
                        aria-label="Retirer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-bone/10 px-6 py-6">
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-bone-muted">{t("cart.total")}</span>
                  <span data-testid="cart-total" className="font-serif text-2xl text-bone">{eur(total)}</span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="w-full border border-brass/60 bg-brass/10 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all duration-300 hover:bg-brass hover:text-ink"
                >
                  {t("cart.checkout")}
                </button>
                <p className="mt-3 text-center font-mono text-[8px] tracking-[0.25em] text-bone-muted">
                  {t("cart.simulated")}
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
